import { useApi } from '@repo-level/core-front';
import { fetchConfig } from 'api/cms/config';
import { isNowBetween } from 'modules/common/helpers';
import type { FC } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IConfigContext } from './helpers';
import { getActiveBrandAsset, initialValues } from './helpers';

export const ConfigContext = createContext<IConfigContext>(null as any);

export const ConfigContextProvider: FC = ({ children }) => {
  const [state, setState] = useState(initialValues);
  const [requestConfig, { data }] = useApi(fetchConfig);

  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    requestConfig(language);
  }, [requestConfig, language]);

  useEffect(() => {
    if (data) {
      setState((prevState) => ({ ...prevState, ...data }));
    }
  }, [data]);

  const isQuestDateValid = useMemo(() => {
    if (state.quest === null) return false;
    return isNowBetween(state.quest.start, state.quest.end);
  }, [state.quest]);

  const isMissionDateValid = useMemo(() => {
    if (state.mission === null) return false;
    return isNowBetween(state.mission.start, state.mission.end);
  }, [state.mission]);

  const isHeadlinePromoDateValid = useMemo(() => {
    if (state.headlinePromo === null) return false;
    return isNowBetween(state.headlinePromo.start, state.headlinePromo.end);
  }, [state.headlinePromo]);

  const isPumpkinRushDateValid = useMemo(() => {
    if (state.pumpkinRush === null) return false;
    return isNowBetween(state.pumpkinRush.start, state.pumpkinRush.end);
  }, [state.pumpkinRush]);

  const activeBrandAsset = useMemo(
    () => getActiveBrandAsset(state.brandAssets),
    [state.brandAssets]
  );

  const value = useMemo(
    () => ({
      ...state,
      isQuestDateValid,
      isMissionDateValid,
      isHeadlinePromoDateValid,
      isPumpkinRushDateValid,
      activeBrandAsset,
    }),
    [
      state,
      isQuestDateValid,
      isMissionDateValid,
      isHeadlinePromoDateValid,
      isPumpkinRushDateValid,
      activeBrandAsset,
    ]
  );

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};
